/**
 * Admin — Nexus v30
 *
 * Unified admin dashboard with 4 tabs:
 *   • Financiero — revenue/cost/margin overview + by-model breakdown
 *   • Usuarios   — table of every user with credits, cost, margin
 *   • Pricing    — live-editable multiplier + per-model prices
 *   • Plantillas — CRUD for Intelligent Templates (v30+)
 *
 * Access: only admins (email starts with 'admin@' — server enforces
 * on every endpoint via `require_admin` dependency).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, DollarSign, Users as UsersIcon, Sliders, Layout,
  Search, TrendingUp, Coins, ChevronRight, Save, X, LifeBuoy,
  Sparkles, Volume2, LayoutDashboard, ShieldCheck, ScrollText, Settings2,
} from 'lucide-react';
import { apiService } from '../services/api';
import { toast } from 'sonner';
import { AdminDashboardCard } from '../components/admin/AdminDashboardCard';
import { MotorFinancieroTab } from '../components/admin/MotorFinancieroTab';

type Tab = 'dashboard' | 'financial' | 'motor' | 'users' | 'pricing' | 'templates' | 'support' | 'audit' | 'logs' | 'settings';

export default function Admin({ defaultTab }: { defaultTab?: Tab } = {}) {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab: Tab = (defaultTab || (params.get('tab') as Tab) || 'dashboard') as Tab;

  const setTab = useCallback((next: Tab) => {
    setParams({ tab: next }, { replace: true });
  }, [setParams]);

  return (
    <div className="min-h-screen bg-slate-950 nx-circuit text-slate-100">
      <header
        className="sticky top-0 z-20 backdrop-blur-md"
        style={{
          background: 'linear-gradient(180deg, hsl(var(--bg-elev) / 0.85), hsl(var(--bg-elev) / 0.4))',
          borderBottom: '1px solid hsl(var(--border))',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => nav('/home')}
            className="inline-flex items-center gap-2 text-sm hover:opacity-90 transition"
            style={{ color: 'hsl(var(--text-muted))' }}
            data-testid="admin-back"
          >
            <ArrowLeft size={16} /> Volver
          </button>
          <div className="text-xs tracking-[0.24em] uppercase font-semibold" style={{ color: 'hsl(var(--accent))' }}>
            Administración
          </div>
        </div>
        <nav className="max-w-7xl mx-auto px-4 flex gap-1 overflow-x-auto">
          {([
            ['dashboard', 'Dashboard', LayoutDashboard],
            ['users', 'Usuarios', UsersIcon],
            ['motor', 'Motor financiero', Sliders],
            ['financial', 'Reportes', DollarSign],
            ['pricing', 'Modelos', Sliders],
            ['templates', 'Plantillas', Layout],
            ['support', 'Soporte IA', LifeBuoy],
            ['audit', 'Auditoría', ShieldCheck],
            ['logs', 'Logs', ScrollText],
            ['settings', 'Configuración', Settings2],
          ] as [Tab, string, any][]).map(([id, label, Icon]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`admin-tab-${id}`}
              className="inline-flex items-center gap-2 px-4 py-3 text-sm transition-colors border-b-2"
              style={{
                borderColor: tab === id ? 'hsl(var(--accent))' : 'transparent',
                color: tab === id ? 'hsl(var(--accent))' : 'hsl(var(--text-muted))',
              }}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {tab === 'dashboard' && <AdminDashboardCard />}
        {tab === 'motor' && <MotorFinancieroTab />}
        {tab === 'financial' && <FinancialTab />}
        {tab === 'users' && <UsersTab />}
        {tab === 'pricing' && <PricingTab />}
        {tab === 'templates' && <TemplatesTab />}
        {tab === 'support' && <SupportTab />}
        {(tab === 'audit' || tab === 'logs' || tab === 'settings') && (
          <ComingSoonBlock
            title={tab === 'audit' ? 'Auditorías' : tab === 'logs' ? 'Logs del sistema' : 'Configuración avanzada'}
            description={tab === 'audit' ? 'Trazabilidad completa de cambios sensibles con firma criptográfica.' : tab === 'logs' ? 'Stream de logs por servicio, con filtros por nivel, workspace y usuario.' : 'Preferencias globales del entorno (seguridad, feature flags, rate limits).'}
          />
        )}
      </main>
    </div>
  );
}

const ComingSoonBlock: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <div
    className="rounded-2xl p-10 text-center max-w-2xl mx-auto"
    style={{
      background: 'linear-gradient(140deg, hsl(var(--bg-elev) / 0.9), hsl(var(--bg-elev) / 0.5))',
      border: '1px solid hsl(var(--border))',
      backdropFilter: 'blur(16px)',
    }}
  >
    <div
      className="w-14 h-14 rounded-2xl mx-auto mb-3 grid place-items-center"
      style={{
        background: 'hsl(var(--accent) / 0.14)',
        border: '1px solid hsl(var(--accent) / 0.3)',
        color: 'hsl(var(--accent))',
      }}
    >
      <Sparkles size={22} />
    </div>
    <div className="text-slate-100 font-medium">{title}</div>
    <div className="text-sm text-slate-500 mt-2 leading-relaxed">{description}</div>
  </div>
);


// ═══════════════════ Financial Tab ═══════════════════

const FinancialTab: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiService.getAdminFinancial(days);
      setData(r);
    } catch { toast.error('No pudimos cargar el dashboard financiero'); }
    finally { setLoading(false); }
  }, [days]);

  useEffect(() => { void load(); }, [load]);

  if (loading || !data) return <SkeletonGrid />;

  return (
    <div data-testid="admin-financial-tab">
      <div className="flex items-center justify-between mb-6">
        <div className="text-2xl font-semibold text-slate-100">Dashboard Financiero</div>
        <select
          value={days}
          onChange={e => setDays(Number(e.target.value))}
          data-testid="financial-window-select"
          className="rounded-lg bg-slate-900/60 border border-slate-800 text-slate-200 px-3 py-1.5 text-sm"
        >
          <option value={7}>Últimos 7 días</option>
          <option value={30}>Últimos 30 días</option>
          <option value={90}>Últimos 90 días</option>
          <option value={365}>Último año</option>
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetricCard title="Ingresos totales" value={`$${data.revenue_lifetime_usd.toFixed(2)}`} icon={DollarSign} color="cyan" />
        <MetricCard title="Créditos en circulación" value={data.credits_in_circulation.toLocaleString('es-ES', { maximumFractionDigits: 0 })} icon={Coins} color="slate" />
        <MetricCard title="Usuarios activos" value={String(data.users.active)} icon={UsersIcon} color="slate" />
        <MetricCard title="Usuarios pagos" value={`${data.users.paid} / ${data.users.total}`} icon={TrendingUp} color="cyan" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <FinancialCard title="Últimos días" data={data.window} />
        <FinancialCard title="Histórico total" data={data.all_time} />
      </div>

      <section className="rounded-2xl border border-slate-800/70 bg-slate-950/40 p-6">
        <div className="text-xs tracking-[0.22em] uppercase text-cyan-300 font-semibold mb-4">Consumo por modelo · Últimos {days} días</div>
        {data.by_model.length === 0 && (
          <div className="text-sm text-slate-500 text-center py-6">Sin datos en el rango seleccionado.</div>
        )}
        {data.by_model.length > 0 && (
          <table className="w-full text-sm" data-testid="by-model-table">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-800/60">
                <th className="pb-2">Modelo</th>
                <th className="pb-2 text-right">Llamadas</th>
                <th className="pb-2 text-right">Tokens</th>
                <th className="pb-2 text-right">Costo real</th>
                <th className="pb-2 text-right">Cobrado</th>
                <th className="pb-2 text-right">Margen</th>
              </tr>
            </thead>
            <tbody>
              {data.by_model.map((m: any) => (
                <tr key={m.model} className="border-b border-slate-900/60">
                  <td className="py-2 text-slate-200">{m.model}</td>
                  <td className="py-2 text-right tabular-nums">{m.calls}</td>
                  <td className="py-2 text-right tabular-nums text-slate-400">{m.tokens.toLocaleString('es-ES')}</td>
                  <td className="py-2 text-right tabular-nums text-slate-400">${m.real_cost_usd.toFixed(4)}</td>
                  <td className="py-2 text-right tabular-nums text-cyan-300">${m.charged_usd.toFixed(4)}</td>
                  <td className="py-2 text-right tabular-nums text-emerald-400">${(m.charged_usd - m.real_cost_usd).toFixed(4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

const FinancialCard: React.FC<{ title: string; data: any }> = ({ title, data }) => (
  <div className="rounded-2xl border border-slate-800/70 bg-slate-950/40 p-6">
    <div className="text-xs tracking-[0.22em] uppercase text-cyan-300 font-semibold mb-4">{title}</div>
    <div className="grid grid-cols-2 gap-4 text-sm">
      <Field k="Llamadas LLM" v={data.llm_calls?.toLocaleString('es-ES') ?? '0'} />
      <Field k="Tokens" v={(data.tokens_consumed || 0).toLocaleString('es-ES')} />
      <Field k="Costo real" v={`$${(data.real_cost_usd || 0).toFixed(4)}`} />
      <Field k="Cobrado" v={`$${(data.charged_usd || 0).toFixed(4)}`} accent />
      <Field k="Créditos consumidos" v={(data.credits_consumed || 0).toFixed(2)} />
      <Field k="Margen" v={`$${(data.margin_usd || 0).toFixed(4)}`} accent />
    </div>
  </div>
);

const Field: React.FC<{ k: string; v: string; accent?: boolean }> = ({ k, v, accent }) => (
  <div>
    <div className="text-xs text-slate-500 uppercase tracking-wide">{k}</div>
    <div className={accent ? 'text-cyan-300 font-semibold mt-1 tabular-nums' : 'text-slate-200 mt-1 tabular-nums'}>{v}</div>
  </div>
);

const MetricCard: React.FC<{ title: string; value: string; icon: any; color?: string }> = ({ title, value, icon: Icon, color = 'cyan' }) => (
  <div className="rounded-2xl border border-slate-800/70 bg-slate-950/40 p-4 backdrop-blur-md">
    <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wide mb-2">
      <Icon size={12} className={color === 'cyan' ? 'text-cyan-300' : 'text-slate-400'} />
      {title}
    </div>
    <div className={color === 'cyan' ? 'text-2xl font-semibold text-cyan-300 tabular-nums' : 'text-2xl font-semibold text-slate-100 tabular-nums'}>
      {value}
    </div>
  </div>
);

const SkeletonGrid: React.FC = () => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
    {[...Array(8)].map((_, i) => <div key={i} className="h-32 rounded-2xl bg-slate-900/40 animate-pulse" />)}
  </div>
);


// ═══════════════════ Users Tab ═══════════════════

const UsersTab: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await apiService.getAdminUsers(q);
        if (!cancelled) setUsers(r.users);
      } catch { toast.error('No pudimos cargar los usuarios'); }
      finally { if (!cancelled) setLoading(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  return (
    <div data-testid="admin-users-tab">
      <div className="flex items-center justify-between mb-4">
        <div className="text-2xl font-semibold text-slate-100">Usuarios</div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Buscar por email…"
            data-testid="admin-user-search"
            className="pl-9 pr-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-200 text-sm w-72 focus:border-cyan-400 focus:outline-none"
          />
        </div>
      </div>

      <div className="rounded-2xl border border-slate-800/70 bg-slate-950/40 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Cargando…</div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">Sin resultados.</div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-800/60 bg-slate-950/60">
                <th className="p-3">Email</th>
                <th className="p-3 text-right">Balance</th>
                <th className="p-3 text-right">Consumidos</th>
                <th className="p-3 text-right">Tokens</th>
                <th className="p-3 text-right">Costo real</th>
                <th className="p-3 text-right">Cobrado</th>
                <th className="p-3 text-right">Margen</th>
                <th className="p-3 text-right">Suscr.</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b border-slate-900/60 hover:bg-slate-900/30">
                  <td className="p-3 text-slate-200">
                    <div>{u.email}</div>
                    {u.role === 'admin' && <span className="text-[10px] text-cyan-300">ADMIN</span>}
                  </td>
                  <td className="p-3 text-right tabular-nums text-cyan-300">{Math.floor(u.credits_balance).toLocaleString('es-ES')}</td>
                  <td className="p-3 text-right tabular-nums text-slate-400">{(u.credits_consumed || 0).toFixed(2)}</td>
                  <td className="p-3 text-right tabular-nums text-slate-400">{(u.tokens_consumed || 0).toLocaleString('es-ES')}</td>
                  <td className="p-3 text-right tabular-nums text-slate-500">${(u.real_cost_usd || 0).toFixed(3)}</td>
                  <td className="p-3 text-right tabular-nums text-slate-200">${(u.charged_usd || 0).toFixed(3)}</td>
                  <td className="p-3 text-right tabular-nums text-emerald-400">${(u.margin_usd || 0).toFixed(3)}</td>
                  <td className="p-3 text-right">
                    <span className={u.subscription_active ? 'text-cyan-300 text-xs' : 'text-slate-600 text-xs'}>
                      {u.subscription_active ? '● activa' : '—'}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <button
                      onClick={() => setSelected(u)}
                      data-testid={`admin-user-detail-${u.id}`}
                      className="text-slate-500 hover:text-cyan-300"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && <UserDetailModal user={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

const UserDetailModal: React.FC<{ user: any; onClose: () => void }> = ({ user, onClose }) => {
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await apiService.getAdminUserUsage(user.id, 100);
        setEvents(r.events);
      } finally { setLoading(false); }
    })();
  }, [user.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center overflow-y-auto" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-950/85 backdrop-blur-md" />
      <div onClick={e => e.stopPropagation()} className="relative w-full max-w-4xl mx-auto px-4 py-8">
        <div className="rounded-2xl border border-cyan-400/25 bg-slate-950/80 backdrop-blur-xl">
          <header className="flex items-center justify-between p-6 border-b border-slate-900/70">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500">Usuario</div>
              <div className="text-lg font-semibold text-slate-100">{user.email}</div>
            </div>
            <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-slate-900 flex items-center justify-center text-slate-400"><X size={14} /></button>
          </header>
          <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4 border-b border-slate-900/70">
            <Field k="Balance" v={String(Math.floor(user.credits_balance))} accent />
            <Field k="Consumidos" v={(user.credits_consumed || 0).toFixed(2)} />
            <Field k="Tokens" v={(user.tokens_consumed || 0).toLocaleString('es-ES')} />
            <Field k="Margen $" v={`$${(user.margin_usd || 0).toFixed(4)}`} accent />
          </div>
          <div className="p-6">
            <div className="text-xs tracking-[0.22em] uppercase text-cyan-300 font-semibold mb-3">Historial de consumo LLM</div>
            {loading ? <div className="text-sm text-slate-500 text-center py-4">Cargando…</div>
              : events.length === 0 ? <div className="text-sm text-slate-500 text-center py-4">Sin actividad LLM aún.</div>
                : (
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full text-xs" data-testid="user-usage-table">
                      <thead className="sticky top-0 bg-slate-950">
                        <tr className="text-left text-slate-500 border-b border-slate-800/60">
                          <th className="pb-2">Fecha</th>
                          <th className="pb-2">Modelo</th>
                          <th className="pb-2 text-right">Tokens</th>
                          <th className="pb-2 text-right">Costo</th>
                          <th className="pb-2 text-right">Cobrado</th>
                          <th className="pb-2 text-right">Créd.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {events.map((e: any) => (
                          <tr key={e.id} className="border-b border-slate-900/40">
                            <td className="py-1.5 text-slate-500 whitespace-nowrap">{new Date(e.created_at).toLocaleString('es-ES')}</td>
                            <td className="py-1.5 text-slate-300">{e.model}</td>
                            <td className="py-1.5 text-right tabular-nums text-slate-400">{e.total_tokens}</td>
                            <td className="py-1.5 text-right tabular-nums text-slate-500">${e.real_cost_usd.toFixed(4)}</td>
                            <td className="py-1.5 text-right tabular-nums text-slate-200">${e.charged_usd.toFixed(4)}</td>
                            <td className="py-1.5 text-right tabular-nums text-cyan-300">{e.credits_debited.toFixed(3)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
          </div>
        </div>
      </div>
    </div>
  );
};


// ═══════════════════ Pricing Tab ═══════════════════

const PricingTab: React.FC = () => {
  const [state, setState] = useState<any>(null);
  const [dirtyConfig, setDirtyConfig] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await apiService.getAdminPricing();
      setState(r);
      setDirtyConfig(false);
    } catch { toast.error('No pudimos cargar pricing'); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const saveConfig = useCallback(async () => {
    if (!state) return;
    setSaving(true);
    try {
      await apiService.updateAdminPricingConfig({
        global_multiplier: state.config.global_multiplier,
        credit_value_usd: state.config.credit_value_usd,
        welcome_gift_credits: state.config.welcome_gift_credits,
        min_charge_credits: state.config.min_charge_credits,
      });
      toast.success('Pricing actualizado');
      setDirtyConfig(false);
    } catch { toast.error('No pudimos guardar'); }
    finally { setSaving(false); }
  }, [state]);

  const saveModel = useCallback(async (m: any) => {
    try {
      await apiService.upsertAdminModelPrice(m);
      toast.success(`Precio actualizado: ${m.model}`);
    } catch { toast.error('No pudimos guardar el modelo'); }
  }, []);

  if (!state) return <SkeletonGrid />;

  return (
    <div data-testid="admin-pricing-tab">
      <div className="flex items-center justify-between mb-6">
        <div className="text-2xl font-semibold text-slate-100">Motor Financiero · Pricing</div>
        <div className="flex items-center gap-2">
          <SelfRechargeButton />
          <VoiceSelector />
        </div>
      </div>

      <section className="rounded-2xl border border-slate-800/70 bg-slate-950/40 p-6 mb-6">
        <div className="flex items-center justify-between mb-5">
          <div className="text-xs tracking-[0.22em] uppercase text-cyan-300 font-semibold">Configuración global</div>
          {dirtyConfig && (
            <button
              onClick={saveConfig}
              disabled={saving}
              data-testid="admin-save-config"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-400 text-slate-950 text-sm font-semibold hover:bg-cyan-300"
            >
              <Save size={13} /> Guardar
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <NumberField
            label="Multiplicador global (×)"
            value={state.config.global_multiplier}
            testId="config-global-multiplier"
            onChange={v => { setState({ ...state, config: { ...state.config, global_multiplier: v } }); setDirtyConfig(true); }}
            step={0.1}
          />
          <NumberField
            label="Valor del crédito (USD)"
            value={state.config.credit_value_usd}
            testId="config-credit-value"
            onChange={v => { setState({ ...state, config: { ...state.config, credit_value_usd: v } }); setDirtyConfig(true); }}
            step={0.01}
          />
          <NumberField
            label="Créditos de bienvenida"
            value={state.config.welcome_gift_credits}
            testId="config-welcome-gift"
            onChange={v => { setState({ ...state, config: { ...state.config, welcome_gift_credits: Math.floor(v) } }); setDirtyConfig(true); }}
            step={10}
            integer
          />
          <NumberField
            label="Cargo mínimo (créd.)"
            value={state.config.min_charge_credits}
            testId="config-min-charge"
            onChange={v => { setState({ ...state, config: { ...state.config, min_charge_credits: v } }); setDirtyConfig(true); }}
            step={0.001}
          />
        </div>
        <div className="mt-4 text-xs text-slate-500">
          Actualizado por <span className="text-slate-400">{state.config.updated_by || '—'}</span>
          {' · '}
          {state.config.updated_at && new Date(state.config.updated_at).toLocaleString('es-ES')}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-800/70 bg-slate-950/40 overflow-hidden">
        <div className="p-6 pb-3">
          <div className="text-xs tracking-[0.22em] uppercase text-cyan-300 font-semibold">Precios por modelo (USD por 1M tokens)</div>
        </div>
        <table className="w-full text-sm" data-testid="model-prices-table">
          <thead>
            <tr className="text-left text-slate-500 border-y border-slate-800/60">
              <th className="p-3">Modelo</th>
              <th className="p-3">Proveedor</th>
              <th className="p-3 text-right">Input / 1M</th>
              <th className="p-3 text-right">Output / 1M</th>
              <th className="p-3 text-right">Cache / 1M</th>
              <th className="p-3 text-right">Multiplicador</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {state.models.map((m: any, idx: number) => (
              <ModelPriceRow
                key={m.model}
                model={m}
                onSave={saveModel}
                testId={`model-row-${idx}`}
              />
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
};

const NumberField: React.FC<{ label: string; value: number; onChange: (v: number) => void; step: number; testId: string; integer?: boolean }> =
({ label, value, onChange, step, testId, integer }) => (
  <div>
    <label className="block text-xs text-slate-500 uppercase tracking-wide mb-1">{label}</label>
    <input
      type="number" step={step} value={value}
      data-testid={testId}
      onChange={e => onChange(integer ? Math.floor(Number(e.target.value)) : Number(e.target.value))}
      className="w-full rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 px-3 py-2 text-sm tabular-nums focus:border-cyan-400 focus:outline-none"
    />
  </div>
);

const ModelPriceRow: React.FC<{ model: any; onSave: (m: any) => void; testId: string }> = ({ model, onSave, testId }) => {
  const [local, setLocal] = useState({ ...model });
  const [dirty, setDirty] = useState(false);
  return (
    <tr className="border-b border-slate-900/60" data-testid={testId}>
      <td className="p-3 text-slate-200">{local.model}</td>
      <td className="p-3 text-slate-500">{local.provider || '—'}</td>
      {(['input_per_1m', 'output_per_1m', 'cache_read_per_1m'] as const).map(field => (
        <td key={field} className="p-2 text-right">
          <input
            type="number" step={0.01}
            value={local[field] || 0}
            data-testid={`${testId}-${field}`}
            onChange={e => { setLocal({ ...local, [field]: Number(e.target.value) }); setDirty(true); }}
            className="w-24 text-right rounded bg-slate-900/60 border border-slate-800 text-slate-100 px-2 py-1 text-xs tabular-nums focus:border-cyan-400 focus:outline-none"
          />
        </td>
      ))}
      <td className="p-2 text-right">
        <input
          type="number" step={0.1}
          value={local.multiplier_override ?? ''}
          placeholder="global"
          data-testid={`${testId}-multiplier`}
          onChange={e => {
            const val = e.target.value === '' ? null : Number(e.target.value);
            setLocal({ ...local, multiplier_override: val });
            setDirty(true);
          }}
          className="w-20 text-right rounded bg-slate-900/60 border border-slate-800 text-slate-100 px-2 py-1 text-xs tabular-nums placeholder:text-slate-600"
        />
      </td>
      <td className="p-2 text-right">
        {dirty && (
          <button
            onClick={() => { onSave(local); setDirty(false); }}
            data-testid={`${testId}-save`}
            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-cyan-400 text-slate-950 text-xs font-semibold hover:bg-cyan-300"
          >
            <Save size={11} /> OK
          </button>
        )}
      </td>
    </tr>
  );
};


// ═══════════════════ Support Tab ═══════════════════
const SupportTab: React.FC = () => {
  const [q, setQ] = useState('');
  const [users, setUsers] = useState<any[]>([]);
  const [user, setUser] = useState<any | null>(null);
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [ws, setWs] = useState<any | null>(null);
  const [ticket, setTicket] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<any | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (q.length < 2) { setUsers([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await apiService.searchSupportUsers(q);
        if (!cancelled) setUsers(r.users || []);
      } catch {/* noop */}
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  useEffect(() => {
    if (!user) { setWorkspaces([]); return; }
    (async () => {
      try {
        const r = await apiService.getSupportUserWorkspaces(user.id);
        setWorkspaces(r.workspaces || []);
      } catch {/* noop */}
    })();
  }, [user]);

  const analyze = useCallback(async () => {
    if (!user || !ticket) { toast.error('Selecciona un usuario y describe el ticket'); return; }
    setAnalyzing(true); setResult(null);
    try {
      const r = await apiService.analyzeSupportTicket({
        user_id: user.id,
        workspace_id: ws?.id || null,
        ticket_message: ticket,
      });
      setResult(r);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'No pudimos analizar el ticket');
    } finally { setAnalyzing(false); }
  }, [user, ws, ticket]);

  const copyPrompt = () => {
    if (result?.technical_prompt) {
      navigator.clipboard.writeText(result.technical_prompt);
      toast.success('Prompt técnico copiado');
    }
  };

  return (
    <div data-testid="admin-support-tab">
      <div className="text-2xl font-semibold text-slate-100 mb-6">Centro de Soporte IA</div>

      <section className="rounded-2xl border border-slate-800/70 bg-slate-950/40 p-6 mb-6">
        <div className="text-xs tracking-[0.22em] uppercase text-cyan-300 font-semibold mb-4">1. Selecciona usuario</div>
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Buscar por email…"
          data-testid="support-user-search"
          className="w-full rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 px-3 py-2 text-sm focus:border-cyan-400 focus:outline-none"
        />
        {users.length > 0 && (
          <div className="mt-2 rounded-lg border border-slate-800 bg-slate-950 overflow-hidden">
            {users.map(u => (
              <button
                key={u.id}
                onClick={() => { setUser(u); setUsers([]); setQ(u.email); }}
                data-testid={`support-user-${u.id}`}
                className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-cyan-500/10 border-b border-slate-900/60 last:border-b-0"
              >
                {u.email} <span className="text-slate-500">· {u.name || '—'}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      {user && (
        <section className="rounded-2xl border border-slate-800/70 bg-slate-950/40 p-6 mb-6">
          <div className="text-xs tracking-[0.22em] uppercase text-cyan-300 font-semibold mb-4">2. Proyecto (opcional)</div>
          {workspaces.length === 0 ? (
            <div className="text-sm text-slate-500">Sin proyectos.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {workspaces.map(w => (
                <button
                  key={w.id}
                  onClick={() => setWs(ws?.id === w.id ? null : w)}
                  data-testid={`support-ws-${w.id}`}
                  className={
                    'text-left rounded-lg border px-3 py-2 text-sm transition ' +
                    (ws?.id === w.id
                      ? 'border-cyan-400 bg-cyan-500/10 text-cyan-100'
                      : 'border-slate-800 bg-slate-950 text-slate-300 hover:border-cyan-400/40')
                  }
                >
                  <div className="font-medium">{w.name || w.id}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{w.type || 'app'} · {w.status || 'idle'}</div>
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {user && (
        <section className="rounded-2xl border border-slate-800/70 bg-slate-950/40 p-6 mb-6">
          <div className="text-xs tracking-[0.22em] uppercase text-cyan-300 font-semibold mb-4">3. Ticket del cliente</div>
          <textarea
            value={ticket}
            onChange={e => setTicket(e.target.value)}
            placeholder="Pega aquí el mensaje del cliente…"
            data-testid="support-ticket-message"
            rows={5}
            className="w-full rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 px-3 py-2 text-sm focus:border-cyan-400 focus:outline-none resize-none"
          />
          <button
            onClick={analyze}
            disabled={analyzing || !ticket}
            data-testid="support-analyze-btn"
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-cyan-400 to-cyan-300 text-slate-950 font-semibold px-5 py-2.5 hover:from-cyan-300 hover:to-cyan-200 disabled:opacity-60"
          >
            <Sparkles size={14} />
            {analyzing ? 'Analizando…' : 'Analizar y generar respuesta'}
          </button>
        </section>
      )}

      {result && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <section className="rounded-2xl border border-cyan-400/25 bg-slate-950/60 p-6" data-testid="support-customer-reply">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs tracking-[0.22em] uppercase text-cyan-300 font-semibold">Respuesta para el cliente</div>
              <button
                onClick={() => { navigator.clipboard.writeText(result.customer_reply); toast.success('Copiado'); }}
                className="text-xs text-cyan-300 hover:text-cyan-200"
              >Copiar</button>
            </div>
            <div className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{result.customer_reply}</div>
          </section>
          <section className="rounded-2xl border border-cyan-400/25 bg-slate-950/60 p-6" data-testid="support-technical-prompt">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs tracking-[0.22em] uppercase text-cyan-300 font-semibold">Prompt técnico (para el Workspace del cliente)</div>
              <button
                onClick={copyPrompt}
                data-testid="support-copy-prompt"
                className="text-xs text-cyan-300 hover:text-cyan-200"
              >Copiar prompt</button>
            </div>
            <div className="text-xs text-slate-300 font-mono whitespace-pre-wrap leading-relaxed max-h-96 overflow-y-auto">
              {result.technical_prompt}
            </div>
            {result.root_causes?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-900/70">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Causas raíz</div>
                <ul className="text-xs text-slate-400 space-y-1 list-disc pl-4">
                  {result.root_causes.map((c: string, i: number) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};


// ═══════════════════ Templates Tab (v30 placeholder — backend arriving) ═══

const TemplatesTab: React.FC = () => {
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<any | null>(null); // null|{}=create, {id}=edit
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<any | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await apiService.listAdminTemplates();
      setTemplates(r.templates || []);
    } catch { /* no-op */ }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const saveTemplate = async (data: any) => {
    setSaving(true);
    try {
      const payload = {
        name: data.name,
        category: data.category,
        description: data.description || '',
        master_prompt: data.master_prompt,
        internal_prompt: data.internal_prompt || '',
        palette: (data.palette_str || '').split(',').map((s: string) => s.trim()).filter(Boolean),
        reference_images: (data.reference_images_str || '').split(/\r?\n/).map((s: string) => s.trim()).filter(Boolean),
        is_active: data.is_active !== false,
        components: [],
        typography: data.typography || '',
        ux_rules: [],
        animations: '',
        metadata: {},
      };
      if (data.id) {
        await apiService.updateAdminTemplate(data.id, payload);
        toast.success('Plantilla actualizada');
      } else {
        await apiService.createAdminTemplate(payload);
        toast.success('Plantilla creada');
      }
      setEditing(null);
      await reload();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'No pude guardar la plantilla');
    } finally { setSaving(false); }
  };

  const toggleActive = async (tpl: any) => {
    try {
      await apiService.setAdminTemplateActive(tpl.id, !tpl.is_active);
      await reload();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'No pude cambiar el estado');
    }
  };

  const duplicate = async (tpl: any) => {
    try {
      await apiService.duplicateAdminTemplate(tpl.id);
      toast.success('Plantilla duplicada');
      await reload();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'No pude duplicar');
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await apiService.deleteAdminTemplate(confirmDelete.id);
      toast.success('Plantilla eliminada');
      setConfirmDelete(null);
      await reload();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'No pude eliminar');
    }
  };

  return (
    <div data-testid="admin-templates-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-2xl font-semibold text-slate-100">Plantillas inteligentes</div>
          <div className="text-sm text-slate-500 mt-1">
            {templates.length} plantilla{templates.length === 1 ? '' : 's'} · gestión completa CRUD
          </div>
        </div>
        <button
          onClick={() => setEditing({})}
          data-testid="tpl-create-btn"
          className="inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold"
          style={{
            background: 'hsl(var(--accent))',
            color: '#031014',
            boxShadow: '0 4px 22px hsl(var(--accent) / 0.35)',
          }}
        >
          + Nueva plantilla
        </button>
      </div>

      {loading ? <div className="text-sm text-slate-500">Cargando…</div>
        : templates.length === 0 ? (
          <div className="rounded-2xl border border-slate-800/70 bg-slate-950/40 p-8 text-center">
            <Layout size={28} className="mx-auto text-slate-600 mb-3" />
            <div className="text-slate-300 font-medium">Aún no hay plantillas</div>
            <div className="text-sm text-slate-500 mt-2">Crea la primera con el botón «Nueva plantilla».</div>
          </div>
        ) : (
          <div className="rounded-2xl overflow-hidden border" style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--bg-elev) / 0.5)' }}>
            {/* Table header */}
            <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,90px)_minmax(0,120px)_minmax(0,200px)] gap-3 px-4 py-3 border-b text-[10px] uppercase tracking-widest opacity-60"
                 style={{ borderColor: 'hsl(var(--border))' }}>
              <div>Plantilla</div><div>Categoría</div><div className="text-right">Usada</div><div>Estado</div><div className="text-right">Acciones</div>
            </div>
            {templates.map((tpl: any) => (
              <div
                key={tpl.id}
                data-testid={`tpl-card-${tpl.id}`}
                className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,90px)_minmax(0,120px)_minmax(0,200px)] gap-3 items-center px-4 py-3 border-b last:border-b-0 hover:bg-white/[0.02] transition"
                style={{ borderColor: 'hsl(var(--border))' }}
              >
                {/* Plantilla — thumb + name + desc */}
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className="w-10 h-10 rounded-xl grid place-items-center shrink-0"
                    style={{
                      background: (tpl.palette?.[0] || 'hsl(var(--accent))') + '22',
                      border: `1px solid ${tpl.palette?.[0] || 'hsl(var(--accent))'}44`,
                      color: tpl.palette?.[0] || 'hsl(var(--accent))',
                    }}
                  >
                    <Layout size={16} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate" style={{ color: 'hsl(var(--text))' }}>{tpl.name}</div>
                    <div className="text-[11px] opacity-60 truncate">{(tpl.description || '').slice(0, 70)}</div>
                  </div>
                </div>
                {/* Categoría */}
                <div className="text-xs opacity-80 capitalize truncate">{tpl.category}</div>
                {/* Usada */}
                <div className="text-xs text-right font-mono tabular-nums opacity-70">{tpl.usage_count ?? 0}</div>
                {/* Estado */}
                <div>
                  <span
                    className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-md"
                    style={{
                      background: tpl.is_active ? 'hsl(var(--accent) / 0.15)' : 'hsl(0 0% 40% / 0.15)',
                      color: tpl.is_active ? 'hsl(var(--accent))' : 'hsl(var(--text-muted))',
                      border: tpl.is_active ? '1px solid hsl(var(--accent) / 0.35)' : '1px solid hsl(var(--border))',
                    }}
                  >
                    {tpl.is_active ? 'Activa' : 'Inactiva'}
                  </span>
                </div>
                {/* Acciones */}
                <div className="flex justify-end gap-1">
                  <button onClick={() => setEditing({ ...tpl, palette_str: (tpl.palette || []).join(', '), reference_images_str: (tpl.reference_images || []).join('\n') })}
                          className="text-[11px] px-2 py-1 rounded-md hover:bg-white/5" style={{ color: 'hsl(var(--accent))' }}
                          data-testid={`tpl-edit-${tpl.id}`}>Editar</button>
                  <button onClick={() => toggleActive(tpl)}
                          className="text-[11px] px-2 py-1 rounded-md hover:bg-white/5 text-slate-300"
                          data-testid={`tpl-toggle-${tpl.id}`}>{tpl.is_active ? 'Desactivar' : 'Activar'}</button>
                  <button onClick={() => duplicate(tpl)}
                          className="text-[11px] px-2 py-1 rounded-md hover:bg-white/5 text-slate-300"
                          data-testid={`tpl-duplicate-${tpl.id}`}>Duplicar</button>
                  <button onClick={() => setConfirmDelete(tpl)}
                          className="text-[11px] px-2 py-1 rounded-md hover:bg-red-500/10"
                          style={{ color: 'hsl(0 84% 62%)' }}
                          data-testid={`tpl-delete-${tpl.id}`}>Eliminar</button>
                </div>
              </div>
            ))}
          </div>
        )}

      {editing !== null && (
        <TemplateEditor
          template={editing}
          saving={saving}
          onSave={saveTemplate}
          onClose={() => setEditing(null)}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-black/70 backdrop-blur-sm" onClick={() => setConfirmDelete(null)}>
          <div className="w-[420px] max-w-[92vw] rounded-2xl p-6 border" onClick={(e) => e.stopPropagation()}
               style={{ background: 'hsl(var(--bg-elev))', borderColor: 'hsl(var(--border))' }}>
            <div className="text-lg font-medium text-slate-100">Eliminar plantilla</div>
            <div className="mt-2 text-sm text-slate-400">
              «{confirmDelete.name}» se eliminará permanentemente. Esta acción no se puede deshacer.
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button onClick={() => setConfirmDelete(null)} className="px-3 py-1.5 rounded-lg text-sm hover:bg-white/5 text-slate-300">
                Cancelar
              </button>
              <button
                onClick={doDelete}
                data-testid="tpl-confirm-delete"
                className="px-3 py-1.5 rounded-lg text-sm text-white"
                style={{ background: 'hsl(0 84% 62%)' }}
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TemplateEditor: React.FC<{
  template: any;
  saving: boolean;
  onSave: (data: any) => void;
  onClose: () => void;
}> = ({ template, saving, onSave, onClose }) => {
  const [form, setForm] = useState<any>({
    id: template.id || null,
    name: template.name || '',
    category: template.category || 'app',
    description: template.description || '',
    master_prompt: template.master_prompt || '',
    thumbnail_url: (template.reference_images && template.reference_images[0]) || '',
    is_active: template.is_active !== false,
  });
  const set = (k: string, v: any) => setForm((s: any) => ({ ...s, [k]: v }));

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Read as base64 so we don't need a new endpoint — persists inline.
    const reader = new FileReader();
    reader.onload = () => set('thumbnail_url', String(reader.result || ''));
    reader.readAsDataURL(file);
  };

  return (
    // Último Deploy · scroll safety: the modal itself scrolls in its
    // own overlay so long forms (mobile, large screenshots pasted into
    // Master Prompt) never clip the "Guardar cambios" footer.
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className="min-h-full w-full flex items-start sm:items-center justify-center p-4 sm:p-6">
        <div
          onClick={(e) => e.stopPropagation()}
          className="w-[560px] max-w-full rounded-2xl p-6 border my-auto"
          style={{ background: 'hsl(var(--bg-elev))', borderColor: 'hsl(var(--border))' }}
          data-testid="tpl-editor"
        >
        <div className="text-lg font-medium text-slate-100 mb-1">
          {form.id ? 'Editar plantilla' : 'Nueva plantilla'}
        </div>
        <div className="text-xs text-slate-500 mb-4">
          Solo cinco campos: el motor infiere el resto automáticamente.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <TplField label="Nombre">
            <input value={form.name} onChange={(e) => set('name', e.target.value)}
                   data-testid="tpl-field-name"
                   className="w-full bg-black/30 px-3 py-2 rounded-lg outline-none text-slate-100" />
          </TplField>
          <TplField label="Tipo">
            <select value={form.category} onChange={(e) => set('category', e.target.value)}
                    data-testid="tpl-field-category"
                    className="w-full bg-black/30 px-3 py-2 rounded-lg outline-none text-slate-100">
              <option value="app">Aplicación</option>
              <option value="landing">Landing / Sitio</option>
              <option value="dashboard">Dashboard</option>
              <option value="ecommerce">E-commerce</option>
              <option value="portfolio">Portafolio</option>
            </select>
          </TplField>
        </div>
        <TplField label="Descripción breve">
          <textarea rows={2} value={form.description} onChange={(e) => set('description', e.target.value)}
                    placeholder="Una línea que resuma para qué sirve la plantilla."
                    data-testid="tpl-field-description"
                    className="w-full bg-black/30 px-3 py-2 rounded-lg outline-none text-slate-100 resize-none" />
        </TplField>
        <TplField label="Prompt maestro">
          <textarea rows={6} value={form.master_prompt} onChange={(e) => set('master_prompt', e.target.value)}
                    placeholder="Describe con detalle el diseño y comportamiento que Nexus debe reproducir cuando use esta plantilla."
                    data-testid="tpl-field-master"
                    className="w-full bg-black/30 px-3 py-2 rounded-lg outline-none text-slate-100 text-sm resize-none" />
        </TplField>
        <TplField label="Imagen de referencia">
          <div className="flex items-start gap-3">
            <div
              className="w-24 h-24 rounded-xl overflow-hidden grid place-items-center shrink-0"
              style={{ background: 'hsl(var(--bg))', border: '1px solid hsl(var(--border))' }}
            >
              {form.thumbnail_url ? (
                <img src={form.thumbnail_url} alt="preview" className="w-full h-full object-cover" />
              ) : (
                <Layout size={22} className="text-slate-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <label
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer text-xs font-medium"
                style={{
                  background: 'hsl(var(--accent) / 0.14)',
                  border: '1px solid hsl(var(--accent) / 0.4)',
                  color: 'hsl(var(--accent))',
                }}
              >
                <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload}
                       data-testid="tpl-field-image" />
                Subir imagen
              </label>
              <div className="text-[11px] text-slate-500 mt-2 leading-relaxed">
                PNG/JPG. Se muestra en la Biblioteca y en el selector automático de plantillas.
              </div>
            </div>
          </div>
        </TplField>
        <label className="mt-3 inline-flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)}
                 data-testid="tpl-field-active" />
          Activa (visible para el selector automático)
        </label>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-sm hover:bg-white/5 text-slate-300">
            Cancelar
          </button>
          <button
            onClick={() => onSave({
              ...form,
              // Adapt to the payload shape expected by saveTemplate.
              palette_str: '',
              reference_images_str: form.thumbnail_url || '',
              typography: '',
              internal_prompt: '',
            })}
            disabled={saving || !form.name || !form.master_prompt}
            data-testid="tpl-save-btn"
            className="px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-40"
            style={{ background: 'hsl(var(--accent))', color: '#031014' }}
          >
            {saving ? 'Guardando…' : (form.id ? 'Guardar cambios' : 'Crear plantilla')}
          </button>
        </div>
        </div>
      </div>
    </div>
  );
};

const TplField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="mt-3">
    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
    {children}
  </div>
);



// ═══════════════════ Admin extras (self-recharge + TTS) ═══════════════════

const SelfRechargeButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const packages = [
    { id: 'credits_100', label: '+100 créditos ($20 simulado)' },
    { id: 'credits_500', label: '+500 créditos ($100 simulado)' },
    { id: 'credits_1250', label: '+1250 créditos ($250 simulado)' },
    { id: 'credits_5000', label: '+5000 créditos ($1000 simulado)' },
  ];
  const recharge = async (pkg: string) => {
    setPending(true);
    try {
      const r = await apiService.adminSelfRecharge(pkg);
      toast.success(`+${r.credits_added} créditos · nuevo balance ${Math.floor(r.balance_after)}`);
      setOpen(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'No pudimos recargar');
    } finally { setPending(false); }
  };
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        data-testid="admin-self-recharge-btn"
        className="inline-flex items-center gap-2 rounded-lg border border-cyan-400/40 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 px-3 py-1.5 text-xs font-semibold"
      >
        <Coins size={13} /> Admin Pass · Recargar
      </button>
      {open && (
        <div className="absolute right-0 top-10 z-30 w-64 rounded-xl border border-slate-800 bg-slate-950 shadow-xl overflow-hidden">
          {packages.map(p => (
            <button
              key={p.id}
              onClick={() => recharge(p.id)}
              disabled={pending}
              data-testid={`self-recharge-${p.id}`}
              className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-cyan-500/10 border-b border-slate-900 last:border-b-0"
            >
              {p.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const VoiceSelector: React.FC = () => {
  const [voices, setVoices] = useState<any[]>([]);
  const [current, setCurrent] = useState<string>('nova');
  useEffect(() => {
    (async () => {
      try {
        const r = await apiService.getTtsVoices();
        setVoices(r.voices);
        setCurrent(r.default_voice_id);
      } catch { /* noop */ }
    })();
  }, []);
  const change = async (id: string) => {
    try {
      await apiService.setDefaultVoice(id);
      setCurrent(id);
      toast.success('Voz por defecto actualizada');
    } catch { toast.error('No pudimos cambiar la voz'); }
  };
  if (voices.length === 0) return null;
  return (
    <div className="inline-flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-1.5 text-xs">
      <Volume2 size={13} className="text-cyan-300" />
      <span className="text-slate-500">Voz</span>
      <select
        value={current}
        onChange={e => change(e.target.value)}
        data-testid="admin-voice-selector"
        className="bg-transparent text-cyan-200 focus:outline-none"
      >
        {voices.map(v => <option key={v.id} value={v.id} className="bg-slate-900">{v.name}</option>)}
      </select>
    </div>
  );
};
